class InsightsQuery {
  constructor(url, options) {
    this.url = url;
    this.options = options;

    this.offset = options.offset || 0;
    this.limit = options.limit || 10;
    this.category = options.category || 'all';
    this.categoryId = options.categoryId || null;
    this.search = options.search || '';

    this.updateQuery();
  }

  async loadPosts(callback) {
    const _this = this;
    const callOptions = {
      method: 'POST',
      headers: {
        Authorization: 'Bearer u7DnP8n54hcJQ3yVIIB2IdRYsWqiqrhP',
        Accept: 'application/json',
        'Content-Type': 'application/graphql',
      },
      body: this.query,
    };

    fetch(this.url, callOptions)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.data || !data.data.entries) {
          console.error('Unexpected GraphQL response:', data);
          throw new Error('Invalid response structure from GraphQL API');
        }

        let html = '';

        for (const entry of data.data.entries) {
          const thumbUrl =
            entry.thumbnail && entry.thumbnail[0] ? entry.thumbnail[0].url : null;

          // Cycle through aspect ratios and spans for visual masonry-style variety
          const aspectClass = aspectRatioSequence[loadMoreCardIndex % aspectRatioSequence.length];
          const layout = cardLayoutSequence[loadMoreCardIndex % cardLayoutSequence.length];
          loadMoreCardIndex++;

          // Format postDate (Craft GraphQL commonly returns an ISO date string)
          let dateHtml = '';
          if (entry.postDate) {
            const d = parseCraftDate(entry.postDate);
            if (!Number.isNaN(d.getTime())) {
              const formatted = d.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
              const iso = d.toISOString().slice(0, 10);
              dateHtml = `<time class="font-rosario text-black" datetime="${iso}" style="font-size:var(--text--2)">${formatted}</time>`;
            }
          }

          const thumbnailHtml = thumbUrl
            ? `<img src="${thumbUrl}" alt="${escapeHtml(entry.title)}" loading="lazy"
                 class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105">`
            : `<div class="w-full h-full bg-linear-to-br from-navy/5 to-indigo/10 flex items-center justify-center">
                 <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 text-navy" fill="none" viewBox="0 0 48 48">
                   <rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" stroke-width="1.5"/>
                   <path d="M6 32l10-10 8 8 6-6 12 12" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                   <circle cx="17" cy="19" r="3.5" stroke="currentColor" stroke-width="1.5"/>
                 </svg>
               </div>`;

          html += `
            <article class="group ${layout}">
              <a href="${entry.url}" class="flex flex-col gap-s no-underline">
                <div class="overflow-hidden rounded-sm bg-navy/10 ${aspectClass}">
                  ${thumbnailHtml}
                </div>
                <div class="flex flex-col gap-2xs">
                  <h2 class="font-nine-elms text-navy group-hover:text-indigo transition-colors duration-200 leading-snug m-0"
                    style="font-size:var(--text-1)">${escapeHtml(entry.title)}</h2>
                  ${dateHtml}
                </div>
              </a>
            </article>
          `;
        }

        if (data.data.entries.length < _this.limit) {
          _this.updateOffset(_this.offset + data.data.entries.length);
        } else {
          _this.updateOffset(_this.offset + _this.limit);
        }

        callback(html, data.data.entries.length);
      })
      .catch(function (error) {
        console.error('Error loading posts:', error);
        callback(
          '<p class="font-rosario text-navy py-l text-center" style="font-size:var(--text-0)">Failed to load posts. Please try again.</p>',
          0
        );
      });
  }

  updateOffset(offset) {
    this.offset = offset;
    this.updateQuery();
  }

  updateQuery() {
    this.query = this.createQuery({
      offset: this.offset,
      limit: this.limit,
      categoryId: this.categoryId,
      search: this.search,
    });
  }

  createQuery(options) {
    // insightsCategories is an Entries field (structure section), so filter by element ID
    const relatedString =
      options.categoryId ? `, relatedTo: [${options.categoryId}]` : '';
    const searchString =
      options.search ? `, search:"${options.search}"` : '';

    return `{
      entries(
        section: "insightEntries",
        orderBy: "postDate desc",
        limit: ${options.limit},
        offset: ${options.offset}
        ${relatedString}
        ${searchString}
      ) {
        title
        slug
        url
        postDate
        ... on post_Entry {
          thumbnail {
            url
          }
        }
      }
    }`;
  }
}

// ——————————————————————————————————————————————————
// Helpers
// ——————————————————————————————————————————————————

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function parseCraftDate(value) {
  if (value == null) return new Date(NaN);

  // If GraphQL ever returns a numeric timestamp, accept either seconds or ms.
  if (typeof value === 'number') {
    const ms = value < 10_000_000_000 ? value * 1000 : value;
    return new Date(ms);
  }

  // Also handle numeric strings.
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && /^[0-9]+(\.[0-9]+)?$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n < 10_000_000_000 ? n * 1000 : n;
      return new Date(ms);
    }
    return new Date(trimmed);
  }

  return new Date(NaN);
}

// Aspect ratio sequence to match server-rendered masonry variety
const aspectRatioSequence = ['aspect-[3/4]', 'aspect-video', 'aspect-square', 'aspect-video', 'aspect-[4/5]', 'aspect-video'];
const cardLayoutSequence = [
  'sm:col-span-1 lg:col-span-1',
  'sm:col-span-1 lg:col-span-2',
  'sm:col-span-1 lg:col-span-1',
  'sm:col-span-2 lg:col-span-1',
  'sm:col-span-1 lg:col-span-1',
  'sm:col-span-1 lg:col-span-2',
];
let loadMoreCardIndex = 0;

// ——————————————————————————————————————————————————
// Initialisation
// ——————————————————————————————————————————————————

const categorySelect = document.getElementById('category-select');
const searchField = document.getElementById('search-field');
const loadPostsButton = document.getElementById('load-posts-button');
const loadMoreWrap = document.getElementById('load-more-wrap');
const postContainer = document.querySelector('.post-container');
const configElement = document.getElementById('insights-config');
const config = configElement ? JSON.parse(configElement.textContent) : {};
const postsToLoad = config.postsToLoad || 15;

loadMoreCardIndex = postContainer ? postContainer.children.length : 0;

let query = new InsightsQuery(config.url || '/api', {
  offset: postsToLoad,
  limit: postsToLoad,
  category: config.currentCategory,
  categoryId: config.currentCategoryId,
});

if (loadPostsButton) {
  loadPostsButton.addEventListener('click', function () {
    loadPostsButton.disabled = true;
    loadPostsButton.textContent = 'Loading…';

    query.loadPosts(function (html, numberOfPosts) {
      postContainer.innerHTML += html;
      loadPostsButton.disabled = false;
      loadPostsButton.textContent = 'Load more insights';

      if (numberOfPosts < postsToLoad && loadMoreWrap) {
        loadMoreWrap.style.display = 'none';
      }
    });
  });
}

// ——————————————————————————————————————————————————
// Category select → navigate to category page
// ——————————————————————————————————————————————————

if (categorySelect) {
  categorySelect.addEventListener('change', function () {
    const searchString = searchField?.value ? '&s=' + encodeURIComponent(searchField.value) : '';

    if (categorySelect.value && categorySelect.value !== 'all') {
      window.location.href = '/insights/' + categorySelect.value + searchString;
    } else {
      window.location.href = '/insights' + (searchString ? '?' + searchString.slice(1) : '');
    }
  });
}

// ——————————————————————————————————————————————————
// Search form submission
// ——————————————————————————————————————————————————

function searchInsights() {
  if (searchField.value != null) {
    const categoryString =
      categorySelect.value && categorySelect.value !== 'all'
        ? '&category=' + encodeURIComponent(categorySelect.value)
        : '';
    window.location.href = '/insights?s=' + encodeURIComponent(searchField.value) + categoryString;
  }
  return false;
}
